export type EmbeddingOrigin =
  | { state: 'embedded'; ancestors: readonly string[] }
  | { state: 'not-embedded' }
  | { state: 'undeterminable' };

/**
 * Reads the chain of origins between the visitor and us, outermost last.
 *
 * Mirrors `useTxOrigin` in frontend-core, with one deliberate difference: an
 * ancestor list that exists but is empty is taken at its word here, where a
 * truthiness check would have fallen through to the referrer.
 *
 * Our own origin is never an embedder, from either signal. Framed and doing
 * same-origin navigation, the referrer is our own previous page, and reading
 * that as an ancestor made the guard refuse the app to itself.
 */
function readAncestors(win: Window): string[] {
  const { ancestorOrigins, origin } = win.location;

  if (ancestorOrigins) {
    return Array.from(ancestorOrigins).filter(
      (ancestor) => ancestor !== origin
    );
  }

  // Firefox exposes no ancestor chain at all. A referrer describes a single
  // hop, so a nested embed is only ever partly visible there.
  try {
    const referrerOrigin = new URL(win.document.referrer).origin;

    return referrerOrigin === origin ? [] : [referrerOrigin];
  } catch {
    return [];
  }
}

export function resolveEmbeddingOrigin(win?: Window): EmbeddingOrigin {
  const target = win ?? (typeof window === 'undefined' ? undefined : window);

  // The static export renders every component once with no window at all, and
  // there is no frame to inspect from there.
  if (!target) {
    return { state: 'not-embedded' };
  }

  if (target.top === target.self) {
    return { state: 'not-embedded' };
  }

  const ancestors = readAncestors(target);

  // Framed, but nothing readable about by whom. Kept distinct from an empty
  // chain so the caller can treat "cannot tell" differently from "nobody".
  return ancestors.length > 0
    ? { state: 'embedded', ancestors }
    : { state: 'undeterminable' };
}
